<template>
  <n-modal 
    v-model:show="showModal" 
    :mask-closable="false"
    :close-on-esc="false"
    class="w-full max-w-2xl"
  >
    <n-card
      title="Carbon Footprint Results"
      :bordered="false"
      size="huge"
      role="dialog"
      aria-modal="true"
      class="w-full max-w-2xl mx-4"
    >
      <template #header-extra>
        <n-button 
          quaternary 
          circle 
          @click="handleClose"
        >
          <template #icon>
            <n-icon><CloseIcon /></n-icon>
          </template>
        </n-button>
      </template>
    <div class="text-center space-y-6 p-4">
        <!-- 主要结果显示 -->
        <div class="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-8">
          <div class="text-6xl mb-4">🌍</div>
          <h2 class="text-4xl font-bold text-gray-900 mb-2">
            {{ formatCarbonFootprint(result.total_carbon_footprint) }}
          </h2>
          <p class="text-xl text-gray-600 mb-2">ton CO₂e / year</p>
          <p class="text-lg text-gray-500">Personal Carbon Footprint</p>
        </div>

        <!-- 分类统计图表 -->
        <div v-if="generateCategoryData().length > 0" class="bg-white rounded-xl border border-gray-200 p-6">
          <h3 class="text-xl font-semibold text-gray-900 mb-4">Carbon Emissions by Category</h3>
          <div class="flex flex-col items-center">
            <div ref="chartContainer" class="w-80 h-80"></div>
            <!-- 图例 -->
            <div class="mt-4 grid grid-cols-2 gap-3 w-full max-w-sm">
              <div v-for="item in generateCategoryData()" :key="item.name" 
                   class="flex items-center gap-2">
                <div 
                  class="w-4 h-4 rounded-full flex-shrink-0"
                  :style="{ backgroundColor: item.color }"
                ></div>
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium text-gray-900 truncate">
                    {{ item.name }}
                  </div>
                  <div class="text-xs text-gray-500">
                    {{ item.value.toFixed(1) }} tCO₂e
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </n-card>
  </n-modal>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted } from 'vue'
// import { useRouter } from 'vue-router'
import { NModal, NCard, NButton, NIcon } from 'naive-ui'
import { CloseOutline as CloseIcon } from '@vicons/ionicons5'
import * as echarts from 'echarts'

interface CategoryData {
  name: string
  value: number
  color: string
}

interface QuestionnaireResult {
  response_id: number
  total_carbon_footprint: number
  session_id: string
  category_breakdown?: Record<string, number>
}

interface Props {
  show: boolean
  result: QuestionnaireResult
  allowClose?: boolean
}

interface Emits {
  (e: 'update:show', value: boolean): void
  (e: 'close'): void
}

const props = withDefaults(defineProps<Props>(), {
  allowClose: true
})

const emit = defineEmits<Emits>()
// const router = useRouter()

const showModal = ref(false)
const chartContainer = ref<HTMLElement>()
let chartInstance: echarts.ECharts | null = null

// 监听show属性变化
watch(() => props.show, (newValue) => {
  console.log('弹窗显示状态变化:', newValue, '结果数据:', props.result)
  showModal.value = newValue
  if (newValue && props.result) {
    console.log('弹窗即将显示，准备初始化图表')
    console.log('结果数据详情:', JSON.stringify(props.result, null, 2))
    // 初始化图表
    initChart()
  }
}, { immediate: true })

// 监听modal显示状态变化
watch(showModal, (newValue) => {
  console.log('Modal内部状态变化:', newValue)
  emit('update:show', newValue)
  if (!newValue) {
    console.log('弹窗关闭，触发close事件')
    emit('close')
  } else if (props.result) {
    console.log('弹窗打开，准备初始化图表')
    console.log('当前结果数据:', props.result)
    // 当弹窗显示时，初始化图表
    initChart()
  }
})

// 监听结果数据变化
watch(() => props.result, (newResult) => {
  console.log('结果数据变化:', newResult)
  if (newResult && showModal.value) {
    console.log('结果数据更新，重新初始化图表')
    initChart()
  }
}, { deep: true })

// 格式化碳足迹数值
const formatCarbonFootprint = (value: number): string => {
  // 直接显示年度数值
  return value.toFixed(1)
}

// 生成分类数据
const generateCategoryData = (): CategoryData[] => {
  const breakdown = props.result.category_breakdown || {}
  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', 
    '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F'
  ]
  
  console.log('生成分类数据，原始breakdown:', breakdown)
  console.log('结果对象:', props.result)
  
  let categories: CategoryData[] = []
  
  // 检查是否有有效的分类数据
  if (breakdown && Object.keys(breakdown).length > 0) {
    // 过滤掉值为0或无效的项
    const validEntries = Object.entries(breakdown).filter(([, value]) => {
      const numValue = Number(value)
      return !isNaN(numValue) && numValue > 0
    })
    
    if (validEntries.length > 0) {
      categories = validEntries.map(([name, value], index) => ({
        name: getCategoryDisplayName(name),
        value: Number(value),
        color: colors[index % colors.length]
      }))
      
      console.log('从breakdown生成的分类数据:', categories)
    }
  }

  // 如果没有有效的分类数据，生成基于总值的合理分配
  if (categories.length === 0) {
    const totalValue = Number(props.result.total_carbon_footprint)
    console.log('使用基于总值的分配数据，总碳足迹:', totalValue)
    
    // 确保总值大于0
    if (totalValue > 0) {
      categories = [
        { name: 'Transportation', value: Math.round(totalValue * 0.3 * 100) / 100, color: '#FF6B6B' },
        { name: 'Housing & Energy', value: Math.round(totalValue * 0.25 * 100) / 100, color: '#4ECDC4' },
        { name: 'Food & Diet', value: Math.round(totalValue * 0.2 * 100) / 100, color: '#45B7D1' },
        { name: 'Consumption', value: Math.round(totalValue * 0.15 * 100) / 100, color: '#96CEB4' },
        { name: 'Other', value: Math.round(totalValue * 0.1 * 100) / 100, color: '#FFEAA7' }
      ]
    } else {
      // 如果总值为0，返回空数组，不显示图表
      console.log('总值为0，返回空数组')
      return []
    }
  }

  console.log('最终分类数据:', categories)
  return categories
}

// 获取分类显示名称
const getCategoryDisplayName = (categoryName: string): string => {
  const nameMap: Record<string, string> = {
    'transportation': 'Transportation',
    'housing': 'Housing & Energy', 
    'housing_energy': 'Housing & Energy',
    'food': 'Food & Diet',
    'food_diet': 'Food & Diet',
    'consumption': 'Consumption',
    'energy': 'Energy Usage',
    'waste': 'Waste Management',
    'other': 'Other',
    '1': 'Transportation',
    '2': 'Housing & Energy',
    '3': 'Food & Diet', 
    '4': 'Consumption',
    '5': 'Other'
  }
  
  // 如果是数字ID，直接映射
  if (/^\d+$/.test(categoryName)) {
    return nameMap[categoryName] || `Category ${categoryName}`
  }
  
  // 如果是字符串，先转换为小写并处理
  const normalizedName = categoryName.toLowerCase().replace(/\s+/g, '_')
  return nameMap[normalizedName] || categoryName
}

// 初始化图表
const initChart = async () => {
  const categoryData = generateCategoryData()
  
  // 如果没有分类数据，不初始化图表
  if (categoryData.length === 0) {
    console.log('没有分类数据，跳过图表初始化')
    return
  }

  // 等待DOM更新
  await nextTick()
  
  // 使用更可靠的方式等待容器出现
  let retryCount = 0
  const maxRetries = 20
  
  const waitForContainer = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const checkContainer = () => {
        if (chartContainer.value) {
          resolve()
        } else if (retryCount < maxRetries) {
          retryCount++
          console.log(`等待弹窗图表容器... 重试次数: ${retryCount}`)
          setTimeout(checkContainer, 100)
        } else {
          reject(new Error('弹窗图表容器初始化超时'))
        }
      }
      checkContainer()
    })
  }

  try {
    await waitForContainer()
    
    console.log('初始化弹窗图表...', chartContainer.value)

    // 销毁现有图表实例
    if (chartInstance) {
      chartInstance.dispose()
      chartInstance = null
    }

    chartInstance = echarts.init(chartContainer.value!)
    
    const option = {
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ton CO₂e ({d}%)'
      },
      series: [
        {
          name: 'Carbon Emissions',
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['50%', '40%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 8,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: false
          },
          emphasis: {
            label: {
              show: false
            },
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          data: categoryData.map(item => ({
            value: Number(item.value.toFixed(2)),
            name: item.name,
            itemStyle: {
              color: item.color
            }
          }))
        }
      ]
    }

    console.log('弹窗图表配置:', option)
    chartInstance.setOption(option)
    console.log('弹窗图表初始化完成')
    
    // 确保图表正确渲染
    setTimeout(() => {
      if (chartInstance) {
        chartInstance.resize()
      }
    }, 100)
    
  } catch (error) {
    console.error('弹窗图表初始化失败:', error)
  }
}

// 查看详细结果
// const viewDetailedResults = () => {
//   router.push(`/questionnaire/results/${props.result.response_id}`)
//   handleClose()
// }

// 关闭弹窗
const handleClose = () => {
  if (props.allowClose) {
    showModal.value = false
  }
}

// 组件卸载时清理图表
onMounted(() => {
  // 监听窗口大小变化，重新调整图表
  const handleResize = () => {
    if (chartInstance) {
      chartInstance.resize()
    }
  }
  
  window.addEventListener('resize', handleResize)
  
  // 组件卸载时清理
  return () => {
    window.removeEventListener('resize', handleResize)
    if (chartInstance) {
      chartInstance.dispose()
    }
  }
})
</script>

<style scoped>
.n-card {
  max-height: 90vh;
  overflow-y: auto;
}
</style>